import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Spinner, TextInput,
} from '@shop/ui'

type Rule = {
  step: number
  perStep: number
  startsOn: string
  accruedThrough: string | null
  dailyWage: number
  normalHours: number
  overtimeRate: number
}

type Share = {
  userId: string
  fullNameAr: string
  share: number
  wage: number
  overtimeHours: number
  overtimePay: number
}

type Day = { day: string; netSales: number; pool: number; accrued: boolean; shares: Share[] }

type Total = {
  userId: string
  fullNameAr: string
  amount: number
  days: number
  wages: number
  overtimeHours: number
  overtimePay: number
}

type Daily = { rule: Rule; from: string; to: string; days: Day[]; totals: Total[] }

const money = (value: number) => value.toFixed(3)

function monthStart(offset = 0): string {
  const now = new Date()
  const month = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1))

  return month.toISOString().slice(0, 10)
}

function monthEnd(start: string): string {
  const [y, m] = start.split('-').map(Number)

  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}

/**
 * العمولات — <b>قاعدةُ المالك ٢٠٢٦-١٠-٠٧</b> (<c>0049</c>).
 *
 * <p>
 * «كل 50 مبيعات بوخد 2 والمية 4 … على مجموع المحل وبتنقسم». فعمولةُ اليوم =
 * ⌊صافي مبيعات المحلّ ÷ ٥٠⌋ × ٢، تُقسم على من فتح ورديّةً ذلك اليوم.
 * </p>
 *
 * <p>
 * ⚠ <b>والعمولةُ تُستحقّ هنا وتُدفع من الدرج.</b> اليومُ إذا انتهى يُرحَّل سهمُ
 * كلِّ مداومٍ مستحقًّا له، والدفعُ («مصروفٌ من الدرج ← موظّف») يُنقصه. وما بقي
 * يُقرأ في «الأرصدة».
 * </p>
 */
export default function Commissions() {
  const { can } = useAuth()
  const queries = useQueryClient()
  const [month, setMonth] = useState(monthStart())

  const data = useQuery({
    queryKey: ['commissions-daily', month],
    queryFn: () => api<Daily>(`/api/v1/commissions/daily?from=${month}&to=${monthEnd(month)}`),
    enabled: can(Perm.commissionView),
  })

  if (!can(Perm.commissionView)) {
    return (
      <Panel title="العمولات">
        <Banner tone="warn">هذه الشاشةُ تحتاج صلاحيّةَ «عرضُ العمولات».</Banner>
      </Panel>
    )
  }

  const view = data.data

  return (
    <div className="grid gap-4">
      <Panel title="العمولة اليوميّة">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setMonth(monthStart(-1))}>الشهر الماضي</Button>
          <Button onClick={() => setMonth(monthStart())}>هذا الشهر</Button>
          <span className="tabular text-sm text-ink-soft" dir="ltr">{month.slice(0, 7)}</span>
        </div>

        {view && (
          <p className="mt-2 text-sm">
            كلُّ <b className="tabular">{money(view.rule.step)}</b> من مبيعات المحلّ في اليوم ⇐{' '}
            <b className="tabular">{money(view.rule.perStep)}</b>، تُقسم على المداومين · ويوميّةُ كلِّ مداوم{' '}
            <b className="tabular">{money(view.rule.dailyWage)}</b> · من{' '}
            <span className="tabular" dir="ltr">{view.rule.startsOn}</span>
          </p>
        )}

        <ErrorBanner error={data.error} />
      </Panel>

      {data.isPending ? (
        <Spinner />
      ) : view && (
        <>
          <Panel title="مجموعُ الشهر لكلّ موظّف — يوميّاتٌ وعمولة">
            {view.totals.length === 0 ? (
              <Empty>لا عمولةَ في هذا الشهر بعد.</Empty>
            ) : (
              <ul className="grid gap-1">
                {view.totals.map((t) => (
                  <li key={t.userId} className="flex items-baseline gap-2 rounded-lg border border-line px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">{t.fullNameAr}</span>
                    <span className="text-xs text-ink-soft">{t.days} يومًا</span>
                    <span className="text-xs text-ink-soft">
                      يوميّات <span className="tabular" dir="ltr">{money(t.wages)}</span>
                      {' · '}عمولة <span className="tabular" dir="ltr">{money(t.amount)}</span>
                      {t.overtimeHours > 0 && (
                        <>{' · '}زيادة {t.overtimeHours} س <span className="tabular" dir="ltr">{money(t.overtimePay)}</span></>
                      )}
                    </span>
                    <b className="tabular" dir="ltr">{money(t.wages + t.amount + t.overtimePay)}</b>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-ink-soft">
              الدفعُ من نقطة البيع: «مصروفٌ من الدرج ← موظّف». وما بقي له يظهر في «الأرصدة».
            </p>
          </Panel>

          <Panel title="الأيّام">
            {view.days.length === 0 ? (
              <Empty>لا أيّام.</Empty>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-ink-soft">
                  <tr>
                    <th className="p-1 text-start">اليوم</th>
                    <th className="p-1 text-end">مبيعاتُ المحلّ</th>
                    <th className="p-1 text-end">العمولة</th>
                    <th className="p-1 text-start">المداومون (يوميّة + عمولة)</th>
                  </tr>
                </thead>
                <tbody>
                  {view.days.map((d) => (
                    <tr key={d.day} className="border-t border-line">
                      <td className="tabular p-1" dir="ltr">
                        {d.day}
                        {!d.accrued && <span className="ms-1 text-xs text-warn">(جارٍ)</span>}
                      </td>
                      <td className="tabular p-1 text-end" dir="ltr">{money(d.netSales)}</td>
                      <td className="tabular p-1 text-end font-semibold" dir="ltr">{money(d.pool)}</td>
                      <td className="p-1 text-xs">
                        {d.shares.length === 0
                          ? <span className="text-ink-soft">—</span>
                          : d.shares.map((s) => `${s.fullNameAr} ${money(s.wage + s.overtimePay)} + ${money(s.share)}`).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          {can(Perm.commissionRateEdit) && (
            <RuleEditor
              rule={view.rule}
              onSaved={() => void queries.invalidateQueries({ queryKey: ['commissions-daily'] })}
            />
          )}
        </>
      )}
    </div>
  )
}

function RuleEditor({ rule, onSaved }: { rule: Rule; onSaved: () => void }) {
  const [step, setStep] = useState(String(rule.step))
  const [perStep, setPerStep] = useState(String(rule.perStep))
  const [startsOn, setStartsOn] = useState(rule.startsOn)
  const [dailyWage, setDailyWage] = useState(String(rule.dailyWage))
  const [normalHours, setNormalHours] = useState(String(rule.normalHours))
  const [overtimeRate, setOvertimeRate] = useState(String(rule.overtimeRate))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  const save = async () => {
    setBusy(true)
    setError(null)
    setSaved(false)

    try {
      await api('/api/v1/commissions/rule', {
        method: 'PUT',
        body: {
          step: toNumber(step) ?? 0,
          perStep: toNumber(perStep) ?? 0,
          startsOn,
          dailyWage: toNumber(dailyWage) ?? 0,
          normalHours: toNumber(normalHours) ?? 8,
          overtimeRate: toNumber(overtimeRate) ?? 0,
        },
      })
      setSaved(true)
      onSaved()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title="القاعدة">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="اليوميّة" hint="لكلّ من داوم — حتّى الخارجيّ">
          <NumberInput value={dailyWage} onChange={setDailyWage} />
        </Field>
        <Field label="ساعاتُ اليوم">
          <NumberInput value={normalHours} onChange={setNormalHours} />
        </Field>
        <Field label="الساعةُ الزيادة">
          <NumberInput value={overtimeRate} onChange={setOvertimeRate} />
        </Field>
        <Field label="كلُّ (من المبيعات)">
          <NumberInput value={step} onChange={setStep} />
        </Field>
        <Field label="عمولتُها">
          <NumberInput value={perStep} onChange={setPerStep} />
        </Field>
        <Field label="تُحسب من يوم" hint="ما قبله دُفع من دفتر الإكسل">
          <TextInput value={startsOn} onChange={setStartsOn} placeholder="2026-10-07" />
        </Field>
      </div>

      <p className="mt-2 text-xs text-ink-soft">
        التعديلُ يسري على ما لم يُرحَّل بعد — والأيّامُ المُرحَّلةُ تبقى كما رُحّلت.
      </p>

      <div className="mt-3 flex items-center gap-3">
        <Button tone="primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'يُحفظ…' : 'احفظ القاعدة'}
        </Button>
        {saved && <span className="text-sm text-ok">حُفظت.</span>}
      </div>

      <ErrorBanner error={error} />
    </Panel>
  )
}
